import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Flex, Heading, Text } from '@/components/common';
import { WizardScreen } from '@/components/layout';
import { ContinueButton, OnboardingWizardHeader } from '@/components/onboarding';
import { CityField } from '@/components/profile/CityField';
import { V90_CITY_SUGGESTIONS } from '@/constants';
import { ProfileSchemaKey, useProfileSchema } from '@/lib/validators';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { fieldLabelStyle } from './styles';
import type { OnboardingStepProps } from './types';
import { useSaveAndNext } from './useSaveAndNext';

/** Temporary allowlist typeahead — replace with geocoding before prod. */
export function CityStep({ flow }: OnboardingStepProps) {
  const { t } = useTranslation(['onboarding', 'profile']);
  const city = useOnboardingStore(state => state.city);
  const setCity = useOnboardingStore(state => state.setCity);
  const saveAndNext = useSaveAndNext(flow);
  const [query, setQuery] = useState(city);
  const schema = useProfileSchema([ProfileSchemaKey.CITY]);

  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { city },
    reValidateMode: 'onSubmit',
  });

  const updateCity = (value: string) => {
    setQuery(value);
    setValue('city', value, { shouldDirty: true });
    clearErrors('city');
  };

  const onValid = async (data: z.infer<typeof schema>) => {
    const matched =
      V90_CITY_SUGGESTIONS.find(c => c.toLowerCase() === data.city.toLowerCase()) ?? data.city;
    setCity(matched);
    await saveAndNext();
  };

  return (
    <WizardScreen header={<OnboardingWizardHeader flow={flow} />}>
      <Flex direction="column" gap="4" py="2" pb="5">
        <Heading size="5" weight="light">
          {t('steps.city.title')}
        </Heading>
        <form id="onboarding-city-form" onSubmit={handleSubmit(onValid)} noValidate>
          <Flex direction="column" gap="1">
            <Text size="1" color="gray" style={fieldLabelStyle}>
              {t('steps.city.fieldLabel')}
            </Text>
            <CityField
              value={query}
              onChange={updateCity}
              data-testid="onboarding-city"
              error={errors.city?.message}
            />
          </Flex>
        </form>
        <ContinueButton onClick={() => void handleSubmit(onValid)()}>
          {t('actions.continue')}
        </ContinueButton>
      </Flex>
    </WizardScreen>
  );
}
